import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  ShieldCheck, Layers, ClipboardList, BookOpen, FileText, Target, LayoutDashboard
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const NAV_ITEMS = [
  { to: "/assessment-design",            label: "Readiness",    icon: LayoutDashboard, exact: true,  testid: "adnav-readiness"  },
  { to: "/assessment-design/policies",   label: "Policies",     icon: ShieldCheck,     exact: false, testid: "adnav-policies"   },
  { to: "/assessment-design/schemes",    label: "Schemes",      icon: Layers,          exact: false, testid: "adnav-schemes"    },
  { to: "/assessment-design/blueprints", label: "Blueprints",   icon: ClipboardList,   exact: false, testid: "adnav-blueprints" },
  { to: "/assessment-design/item-banks", label: "Item Banks",   icon: BookOpen,        exact: false, testid: "adnav-item-banks" },
  { to: "/assessment-design/rubrics",    label: "Rubrics",      icon: FileText,        exact: false, testid: "adnav-rubrics"    },
  { to: "/assessment-design/outcomes",   label: "Outcomes",     icon: Target,          exact: false, testid: "adnav-outcomes"   },
];

/** RBAC: which roles can access Assessment Design at all */
const ALLOWED_ROLES = new Set([
  "super_admin", "institution_admin", "programme_manager", "dean",
  "faculty", "instructor", "registrar",
  "assessment_designer", "assessment_reviewer",
]);

/**
 * AssessmentDesignShell — workspace layout wrapper.
 * Renders a left sub-nav + workspace content area.
 * Blocks student access with an explicit 403 card.
 */
export function AssessmentDesignShell({ children, title, breadcrumb }) {
  const { user } = useAuth();
  const location = useLocation();

  if (!user) return null;

  if (!ALLOWED_ROLES.has(user.role)) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-8"
           data-testid="ad-access-denied">
        <div className="max-w-md w-full rounded-xl border border-red-800/50 bg-red-950/20 p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-red-900/40 flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="w-6 h-6 text-red-400" />
          </div>
          <h2 className="text-xl font-semibold text-slate-100 mb-2">Access Denied</h2>
          <p className="text-sm text-slate-400 mb-4">
            Assessment Design is not available for the <strong className="text-slate-300">{user.role}</strong> role.
          </p>
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border border-red-700/60 bg-red-950/50 text-red-300">
            403 Permission Denied
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex" data-testid="assessment-design-shell">
      {/* ── Left sub-nav ────────────────────────────────────────────────── */}
      <aside className="w-52 flex-shrink-0 border-r border-slate-800 bg-slate-900/60 flex flex-col pt-6 pb-8">
        <div className="px-4 mb-6">
          <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">
            Assessment Design
          </span>
        </div>
        <nav className="flex flex-col gap-0.5 px-2">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const active = item.exact
              ? location.pathname === item.to
              : location.pathname.startsWith(item.to) && !item.exact
                ? location.pathname === item.to || location.pathname.startsWith(item.to + "/")
                : false;
            const isActive = item.exact
              ? location.pathname === item.to
              : location.pathname.startsWith(item.to);

            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.exact}
                data-testid={item.testid}
                className={({ isActive: navActive }) =>
                  `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                    navActive
                      ? "bg-sky-900/40 text-sky-300 font-medium"
                      : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/60"
                  }`
                }
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>
      </aside>

      {/* ── Main content ────────────────────────────────────────────────── */}
      <main className="flex-1 overflow-auto">
        {/* Header */}
        <div className="border-b border-slate-800 bg-slate-900/40 px-8 py-5">
          {breadcrumb && (
            <div className="text-xs text-slate-500 mb-1 flex items-center gap-1">
              <span>Teaching &amp; Learning</span>
              <span className="mx-1">›</span>
              <span className="text-slate-400">Assessment Design</span>
              {breadcrumb && (
                <>
                  <span className="mx-1">›</span>
                  <span className="text-sky-400">{breadcrumb}</span>
                </>
              )}
            </div>
          )}
          {title && (
            <h1 className="text-xl font-semibold text-slate-100 tracking-tight">{title}</h1>
          )}
        </div>
        <div className="p-8">{children}</div>
      </main>
    </div>
  );
}

/** Returns true if the current user has the designer capability */
export function useIsDesigner() {
  const { user } = useAuth();
  const DESIGNER = new Set(["super_admin", "institution_admin", "programme_manager", "dean", "assessment_designer"]);
  return user ? DESIGNER.has(user.role) : false;
}

/** Returns true if the current user has the faculty capability */
export function useIsFaculty() {
  const { user } = useAuth();
  const FACULTY = new Set(["super_admin", "institution_admin", "faculty", "instructor", "dean", "programme_manager", "assessment_designer", "assessment_reviewer"]);
  return user ? FACULTY.has(user.role) : false;
}

/** Returns true if the current user has the reviewer capability */
export function useIsReviewer() {
  const { user } = useAuth();
  const REVIEWER = new Set(["super_admin", "institution_admin", "dean", "registrar", "assessment_reviewer"]);
  return user ? REVIEWER.has(user.role) : false;
}
